import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Filter,
  ChevronRight,
  Loader2,
  ExternalLink,
  Check,
  AlertCircle,
  Link2Off,
  Disc,
  ShoppingCart,
  DollarSign,
  ArrowUp,
  ArrowDown,
  Facebook,
  Calendar,
  RefreshCw,
  Tag,
  Truck,
  CheckCircle,
} from 'lucide-react';
import { Pagination } from '../components/common/Pagination';
import { api } from '../services/api';
import {
  ITEM_CATEGORIES,
  ITEM_CONDITIONS,
  ITEM_CONDITION_LABELS,
  EBAY_SITES,
  SELLING_PROVIDERS,
  type ItemCategory,
  type ItemCondition,
  type EbaySiteId,
  type SellingProviderId,
} from '@findmystuff/shared';
import { useSellingStore } from '../store/selling';
import { SellModal } from '../components/selling/SellModal';
import { DiscogsSellModal } from '../components/selling/DiscogsSellModal';
import { FacebookMarketplaceSellModal } from '../components/selling/FacebookMarketplaceSellModal';

const PAGE_SIZE = 25;

type SortOption = 'recent' | 'oldest' | 'title_asc' | 'title_desc' | 'condition_best' | 'condition_worst' | 'price_high' | 'price_low';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'recent', label: 'Recently Added' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'title_asc', label: 'Title A-Z' },
  { value: 'title_desc', label: 'Title Z-A' },
  { value: 'condition_best', label: 'Condition: Best First' },
  { value: 'condition_worst', label: 'Condition: Worst First' },
];

const PROVIDER_ICONS: Record<SellingProviderId, React.ReactNode> = {
  ebay: <ShoppingCart className="w-4 h-4" />,
  discogs: <Disc className="w-4 h-4" />,
  facebook: <Facebook className="w-4 h-4" />,
};

// Format date to human-readable format with time
// Uses calendar day comparison, not time-based difference
const formatDate = (date: string | Date) => {
  const d = new Date(date);
  const now = new Date();

  // Compare calendar dates (midnight-to-midnight), not timestamps
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((todayOnly.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));

  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  if (diffDays === 0) {
    return `Today at ${time}`;
  } else if (diffDays === 1) {
    return `Yesterday at ${time}`;
  } else if (diffDays < 7) {
    return `${diffDays} days ago at ${time}`;
  } else {
    return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${time}`;
  }
};

export function SellingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // URL params for OAuth callbacks
  const ebayConnected = searchParams.get('ebay_connected');
  const ebayError = searchParams.get('ebay_error');
  const discogsConnected = searchParams.get('discogs_connected');
  const discogsError = searchParams.get('discogs_error');

  // Clear OAuth URL params after showing toast (but keep page param)
  useEffect(() => {
    if (ebayConnected || ebayError || discogsConnected || discogsError) {
      const timer = setTimeout(() => {
        setSearchParams((prev) => {
          const params = new URLSearchParams();
          // Keep the page param if it exists
          const pageParam = prev.get('page');
          if (pageParam) params.set('page', pageParam);
          return params;
        });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [ebayConnected, ebayError, discogsConnected, discogsError, setSearchParams]);

  // Sync status message
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Get all filter state from URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const category = (searchParams.get('category') || '') as ItemCategory | '';
  const condition = (searchParams.get('condition') || '') as ItemCondition | '';
  const sort = (searchParams.get('sort') || 'recent') as SortOption;
  const itemFilter = (searchParams.get('filter') || 'all') as 'all' | 'sold' | 'unsold' | 'no_price' | 'not_listed' | 'listed_with_price';
  const selectedInventoryParam = searchParams.get('inventory') || '';
  const providerParam = searchParams.get('provider') as SellingProviderId | null;

  // Use URL param for inventory, with fallback to first inventory
  const [selectedInventory, setSelectedInventoryState] = useState<string>(selectedInventoryParam);

  // Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          params.delete(key);
        } else if (key === 'page' && value === '1') {
          params.delete(key);
        } else if (key === 'sort' && value === 'recent') {
          params.delete(key);
        } else if (key === 'filter' && value === 'all') {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      return params;
    });
  }, [setSearchParams]);

  // Update page in URL
  const setPage = useCallback((newPage: number) => {
    updateParams({ page: String(newPage) });
  }, [updateParams]);

  // Wrapper to update inventory in both state and URL
  const setSelectedInventory = useCallback((value: string) => {
    setSelectedInventoryState(value);
    updateParams({ inventory: value, page: '1' });
  }, [updateParams]);

  // Modal state from store
  const {
    selectedProvider,
    setSelectedProvider,
    selectedEbaySiteId,
    setSelectedEbaySiteId,
    sellModalItemId,
    openSellModal,
    closeSellModal,
  } = useSellingStore();

  // Cache the selected item for modals to prevent unmount during query invalidation
  const [cachedModalItem, setCachedModalItem] = useState<typeof items[number] | null>(null);

  // Fetch inventories
  const { data: inventories } = useQuery({
    queryKey: ['inventories'],
    queryFn: () => api.inventories.list(),
  });

  // Set default inventory from URL or first available
  useEffect(() => {
    if (inventories && inventories.length > 0) {
      if (selectedInventoryParam && inventories.some(inv => inv.id === selectedInventoryParam)) {
        // URL has valid inventory, use it
        setSelectedInventoryState(selectedInventoryParam);
      } else if (!selectedInventory) {
        // No selection yet, use first inventory
        setSelectedInventoryState(inventories[0]!.id);
        updateParams({ inventory: inventories[0]!.id });
      }
    }
  }, [inventories, selectedInventoryParam, selectedInventory, updateParams]);

  // Sync provider from URL to store
  useEffect(() => {
    if (providerParam && providerParam !== selectedProvider) {
      setSelectedProvider(providerParam);
    }
  }, [providerParam, selectedProvider, setSelectedProvider]);

  // Fetch eBay connection status
  const { data: ebayStatus, isLoading: isLoadingEbayStatus } = useQuery({
    queryKey: ['ebay', 'status'],
    queryFn: () => api.ebay.getStatus(),
  });

  // Fetch Discogs connection status
  const { data: discogsStatus, isLoading: isLoadingDiscogsStatus } = useQuery({
    queryKey: ['discogs', 'status'],
    queryFn: () => api.discogs.getStatus(),
  });

  // Convert sort option to API parameters
  const getSortParams = (sortOption: SortOption): { sortBy: string; sortOrder: 'asc' | 'desc' } => {
    switch (sortOption) {
      case 'oldest':
        return { sortBy: 'createdAt', sortOrder: 'asc' };
      case 'price_high':
        return { sortBy: 'discogsPrice', sortOrder: 'desc' };
      case 'price_low':
        return { sortBy: 'discogsPrice', sortOrder: 'asc' };
      case 'title_asc':
        return { sortBy: 'title', sortOrder: 'asc' };
      case 'title_desc':
        return { sortBy: 'title', sortOrder: 'desc' };
      case 'condition_best':
        return { sortBy: 'condition', sortOrder: 'asc' };
      case 'condition_worst':
        return { sortBy: 'condition', sortOrder: 'desc' };
      default:
        return { sortBy: 'createdAt', sortOrder: 'desc' };
    }
  };

  const { sortBy, sortOrder } = getSortParams(sort);

  // Fetch items
  const { data: itemsData, isLoading: isLoadingItems } = useQuery({
    queryKey: ['items', { inventoryId: selectedInventory, search, category, page, sortBy, sortOrder, itemFilter }],
    queryFn: () => {
      const params: Record<string, string | number | boolean> = {
        page,
        pageSize: PAGE_SIZE,
        sortBy,
        sortOrder,
      };
      if (selectedInventory) params.inventoryId = selectedInventory;
      if (search) params.search = search;
      if (category) params.category = category;
      // Apply item filter
      if (itemFilter === 'sold') params.listingStatus = 'sold';
      if (itemFilter === 'no_price') params.hasPrice = false;
      if (itemFilter === 'not_listed') params.hasListing = false;
      if (itemFilter === 'unsold') params.isUnsold = true; // Listed but not sold
      if (itemFilter === 'listed_with_price') {
        params.hasListing = true;
        params.hasPrice = true;
      }
      return api.items.list(params);
    },
    enabled: !!selectedInventory,
  });

  // Fetch count of items needing shipping (for banner)
  const { data: unshippedData } = useQuery({
    queryKey: ['items', { needsShipping: true, countOnly: true }],
    queryFn: () => api.items.list({ needsShipping: true, pageSize: 1 }),
    staleTime: 30 * 1000,
  });
  const unshippedCount = unshippedData?.meta?.totalCount || 0;

  // Get items from API (already sorted server-side)
  const items = itemsData?.items || [];

  // Filter by condition client-side only
  const filteredItems = items.filter((item) => {
    // Condition filter
    if (condition && item.condition !== condition) return false;
    return true;
  });

  // Fetch specific item for modal if not in filtered list (e.g., navigating from Messages page)
  const { data: fetchedModalItem } = useQuery({
    queryKey: ['item', sellModalItemId],
    queryFn: () => api.items.get(sellModalItemId!),
    enabled: !!sellModalItemId && !items.some((i) => i.id === sellModalItemId),
    staleTime: 30000, // Cache for 30 seconds
  });

  const totalPages = itemsData?.meta?.totalPages || 1;
  const totalCount = itemsData?.meta?.totalCount || 0;

  // Total value from API (across all pages)
  const totalDiscogsValue = (itemsData?.meta as { totalValue?: number })?.totalValue || 0;
  const itemsWithPrice = (itemsData?.meta as { pricedItemCount?: number })?.pricedItemCount || 0;

  // Connect to eBay mutation
  const connectEbayMutation = useMutation({
    mutationFn: () => api.ebay.getAuthUrl(selectedEbaySiteId),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
  });

  // Disconnect eBay mutation
  const disconnectEbayMutation = useMutation({
    mutationFn: () => api.ebay.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ebay', 'status'] });
    },
  });

  // Connect to Discogs mutation
  const connectDiscogsMutation = useMutation({
    mutationFn: () => api.discogs.getAuthUrl(),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
  });

  // Disconnect Discogs mutation
  const disconnectDiscogsMutation = useMutation({
    mutationFn: () => api.discogs.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discogs', 'status'] });
    },
  });

  // Sync sales from Discogs mutation
  const syncSalesMutation = useMutation({
    mutationFn: () => api.discogs.syncSales(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      // Also refresh messages data
      queryClient.invalidateQueries({ queryKey: ['discogs-messages'] });
      queryClient.invalidateQueries({ queryKey: ['discogs-unread-count'] });
      setSyncMessage({
        type: 'success',
        text: data.synced > 0
          ? `Synced ${data.synced} sold item${data.synced !== 1 ? 's' : ''} from ${data.total} orders`
          : `No new sales found (checked ${data.total} orders)`,
      });
      setTimeout(() => setSyncMessage(null), 5000);
    },
    onError: () => {
      setSyncMessage({ type: 'error', text: 'Failed to sync sales from Discogs' });
      setTimeout(() => setSyncMessage(null), 5000);
    },
  });

  // Get selected item for modal - check filtered items first, then fetched item
  const selectedItem = sellModalItemId
    ? items.find((i) => i.id === sellModalItemId) || fetchedModalItem
    : null;

  // Cache item when modal opens to prevent unmount during query invalidation
  useEffect(() => {
    if (sellModalItemId && selectedItem) {
      setCachedModalItem(selectedItem);
    } else if (!sellModalItemId) {
      // Clear cache when modal closes
      setCachedModalItem(null);
    }
  }, [sellModalItemId, selectedItem]);

  // Use cached item if selectedItem is temporarily undefined during refetch
  const modalItem = selectedItem || cachedModalItem;

  // Determine if current provider is connected
  // Facebook is always "connected" since it doesn't require OAuth
  const isProviderConnected = selectedProvider === 'facebook'
    ? true
    : selectedProvider === 'ebay'
      ? ebayStatus?.connected
      : discogsStatus?.connected;
  const isLoadingStatus = selectedProvider === 'facebook'
    ? false
    : selectedProvider === 'ebay'
      ? isLoadingEbayStatus
      : isLoadingDiscogsStatus;

  // Reset page when filters change
  const handleSearchChange = (value: string) => {
    updateParams({ search: value, page: '1' });
  };

  const handleCategoryChange = (value: ItemCategory | '') => {
    updateParams({ category: value, page: '1' });
  };

  const handleConditionChange = (value: ItemCondition | '') => {
    updateParams({ condition: value, page: '1' });
  };

  const handleSortChange = (value: SortOption) => {
    updateParams({ sort: value, page: '1' });
  };

  const handleItemFilterChange = (value: 'all' | 'sold' | 'unsold' | 'no_price' | 'not_listed' | 'listed_with_price') => {
    updateParams({ filter: value, page: '1' });
  };

  const handleProviderChange = (value: SellingProviderId) => {
    setSelectedProvider(value);
    updateParams({ provider: value });
  };

  const handleConnect = () => {
    if (selectedProvider === 'ebay') {
      connectEbayMutation.mutate();
    } else {
      connectDiscogsMutation.mutate();
    }
  };

  const handleDisconnect = () => {
    if (selectedProvider === 'ebay') {
      disconnectEbayMutation.mutate();
    } else {
      disconnectDiscogsMutation.mutate();
    }
  };

  const isConnecting = selectedProvider === 'ebay' ? connectEbayMutation.isPending : connectDiscogsMutation.isPending;
  const isDisconnecting = selectedProvider === 'ebay' ? disconnectEbayMutation.isPending : disconnectDiscogsMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Success/Error toasts */}
      {ebayConnected && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5" />
          <span>eBay account connected successfully!</span>
        </div>
      )}
      {ebayError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to connect eBay: {ebayError}</span>
        </div>
      )}
      {discogsConnected && (
        <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg flex items-center gap-3">
          <Check className="w-5 h-5" />
          <span>Discogs account connected successfully!</span>
        </div>
      )}
      {discogsError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to connect Discogs: {discogsError}</span>
        </div>
      )}
      {syncMessage && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          syncMessage.type === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {syncMessage.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{syncMessage.text}</span>
        </div>
      )}

      {/* Needs Shipping Banner */}
      {unshippedCount > 0 && (
        <Link
          to="/selling/shipping"
          className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <Truck className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-amber-800">
              You have {unshippedCount} item{unshippedCount !== 1 ? 's' : ''} to ship
            </span>
          </div>
          <span className="text-sm text-amber-600 group-hover:text-amber-700 font-medium">
            View &rarr;
          </span>
        </Link>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Selling</h1>
          <p className="text-gray-600">List your items for sale</p>
        </div>

        {/* Provider Selection and Connection Status */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Provider Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Provider:</label>
            <select
              value={selectedProvider}
              onChange={(e) => handleProviderChange(e.target.value as SellingProviderId)}
              className="input text-sm py-1.5"
            >
              {SELLING_PROVIDERS.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          {/* Connection Status */}
          {isLoadingStatus ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : selectedProvider === 'facebook' ? (
            // Facebook doesn't need OAuth - always ready
            <span className="flex items-center gap-2 text-sm bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full">
              {PROVIDER_ICONS[selectedProvider]}
              Ready to Export
            </span>
          ) : isProviderConnected ? (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 text-sm bg-green-100 text-green-700 px-3 py-1.5 rounded-full">
                {PROVIDER_ICONS[selectedProvider]}
                {selectedProvider === 'ebay' ? 'eBay' : 'Discogs'} Connected
                {selectedProvider === 'discogs' && discogsStatus?.username && (
                  <span className="text-green-600">({discogsStatus.username})</span>
                )}
              </span>
              {/* Sync Sales button - only for Discogs */}
              {selectedProvider === 'discogs' && (
                <button
                  onClick={() => syncSalesMutation.mutate()}
                  disabled={syncSalesMutation.isPending}
                  className="flex items-center gap-1.5 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  {syncSalesMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Sync Sales
                </button>
              )}
              <button
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {isDisconnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {/* eBay-specific: Site selector */}
              {selectedProvider === 'ebay' && (
                <select
                  value={selectedEbaySiteId}
                  onChange={(e) => setSelectedEbaySiteId(e.target.value as EbaySiteId)}
                  className="input text-sm py-1.5"
                >
                  {EBAY_SITES.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              )}
              <button
                onClick={handleConnect}
                disabled={isConnecting}
                className="btn-primary text-sm py-1.5"
              >
                {isConnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <span className="mr-2">{PROVIDER_ICONS[selectedProvider]}</span>
                )}
                Connect {selectedProvider === 'ebay' ? 'eBay' : 'Discogs'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Inventory Selector */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inventory
            </label>
            <select
              value={selectedInventory}
              onChange={(e) => setSelectedInventory(e.target.value)}
              className="input"
            >
              {inventories?.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.name} ({inv.itemCount} items)
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <Search className="absolute left-3 top-[calc(50%+2px)] w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value as ItemCategory | '')}
              className="input pl-10 pr-8 appearance-none text-sm"
            >
              <option value="">All Categories</option>
              {ITEM_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <select
            value={condition}
            onChange={(e) => handleConditionChange(e.target.value as ItemCondition | '')}
            className="input text-sm"
          >
            <option value="">All Conditions</option>
            {ITEM_CONDITIONS.map((cond) => (
              <option key={cond} value={cond}>
                {ITEM_CONDITION_LABELS[cond]}
              </option>
            ))}
          </select>

          {/* Item filter - only show when Discogs is selected */}
          {selectedProvider === 'discogs' && (
            <select
              value={itemFilter}
              onChange={(e) => handleItemFilterChange(e.target.value as 'all' | 'sold' | 'unsold' | 'no_price' | 'not_listed' | 'listed_with_price')}
              className="input text-sm"
            >
              <option value="all">All Items</option>
              <option value="no_price">No Price Set</option>
              <option value="not_listed">Not Listed</option>
              <option value="listed_with_price">Listed with Price</option>
              <option value="unsold">Not Sold</option>
              <option value="sold">Sold</option>
            </select>
          )}

          <select
            value={sort}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className="input text-sm"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Price Sort Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => handleSortChange(sort === 'price_high' ? 'recent' : 'price_high')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                sort === 'price_high'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              title="Sort by price: High to Low"
            >
              <DollarSign className="w-3.5 h-3.5" />
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleSortChange(sort === 'price_low' ? 'recent' : 'price_low')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                sort === 'price_low'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              title="Sort by price: Low to High"
            >
              <DollarSign className="w-3.5 h-3.5" />
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Total Value Banner - always show when connected */}
      {isProviderConnected && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-emerald-100 text-sm font-medium">
                  {itemFilter === 'all' ? 'Total Inventory Value' :
                   itemFilter === 'sold' ? 'Sold Items Value' :
                   itemFilter === 'unsold' ? 'Listed (Not Sold) Value' :
                   itemFilter === 'no_price' ? 'Items Without Price' :
                   itemFilter === 'not_listed' ? 'Not Listed Items Value' :
                   itemFilter === 'listed_with_price' ? 'Listed with Price Value' :
                   'Filtered Items Value'}
                </p>
                <p className="text-white text-2xl font-bold">
                  {itemFilter === 'no_price' ? `${totalCount} items` : `$${totalDiscogsValue.toFixed(2)}`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-emerald-100 text-sm">
                {itemFilter === 'no_price' ? 'need pricing' : `${itemsWithPrice} items priced`}
              </p>
              <p className="text-white/80 text-xs">
                {itemFilter === 'all' ? `out of ${totalCount} total` : `${totalCount} matching filter`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      {!isProviderConnected ? (
        <div className="card p-8 text-center">
          <Link2Off className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Connect Your {selectedProvider === 'ebay' ? 'eBay' : 'Discogs'} Account
          </h3>
          <p className="text-gray-600 mb-4">
            {selectedProvider === 'ebay'
              ? 'Connect your eBay account to start listing your items for sale.'
              : 'Connect your Discogs account to sell music items on the Discogs marketplace.'}
          </p>
          <button
            onClick={handleConnect}
            disabled={isConnecting}
            className="btn-primary"
          >
            {isConnecting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <span className="mr-2">{PROVIDER_ICONS[selectedProvider]}</span>
            )}
            Connect {selectedProvider === 'ebay' ? 'eBay' : 'Discogs'} Account
          </button>
        </div>
      ) : isLoadingItems ? (
        <div className="card p-8 text-center text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
          Loading items...
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-500">No items found</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {filteredItems.map((item) => (
            <button
              key={item.id}
              onClick={() => openSellModal(item.id)}
              className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 text-left transition-colors"
            >
              <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {item.images?.[0] ? (
                  <img
                    src={item.images[0].thumbnailUrl || item.images[0].url}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <span className="text-2xl text-gray-300">📦</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 truncate">{item.title}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{ITEM_CONDITION_LABELS[item.condition as ItemCondition] || item.condition}</span>
                  <span className="text-gray-300">•</span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(item.createdAt)}
                  </span>
                </div>
                {(item as any).deliveredAt ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded mt-1 font-medium">
                    <CheckCircle className="w-3 h-3" />
                    DELIVERED
                  </span>
                ) : (item as any).shippedAt ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded mt-1 font-medium">
                    <Truck className="w-3 h-3" />
                    SHIPPED
                  </span>
                ) : item.soldAt ? (
                  <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded mt-1 font-medium">
                    <Tag className="w-3 h-3" />
                    SOLD
                  </span>
                ) : item.listingStatus === 'listed' && (
                  <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1">
                    <ExternalLink className="w-3 h-3" />
                    Listed
                  </span>
                )}
              </div>
              {(item as any).discogsPrice > 0 && (
                <div className="flex-shrink-0 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                  ${((item as any).discogsPrice as number).toFixed(2)}
                </div>
              )}
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Pagination */}
      {isProviderConnected && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {/* Sell Modal - eBay */}
      {sellModalItemId && modalItem && selectedProvider === 'ebay' && (
        <SellModal
          item={modalItem}
          isOpen={!!sellModalItemId}
          onClose={closeSellModal}
        />
      )}

      {/* Sell Modal - Discogs */}
      {sellModalItemId && modalItem && selectedProvider === 'discogs' && (
        <DiscogsSellModal
          item={modalItem}
          isOpen={!!sellModalItemId}
          onClose={closeSellModal}
        />
      )}

      {/* Sell Modal - Facebook Marketplace */}
      {sellModalItemId && modalItem && selectedProvider === 'facebook' && (
        <FacebookMarketplaceSellModal
          item={modalItem}
          isOpen={!!sellModalItemId}
          onClose={closeSellModal}
        />
      )}
    </div>
  );
}
